import { useTranslation } from '@/contexts/i18n-context';
import { GradientFormTitle } from '@/components/containers/gradient-form-title';
import { Section } from '@/components/containers/section';
import { PageContainer } from '@/components/containers/page-container';
import { VisitFormButtonRow } from '@/components/containers/visit-form-button-row';
import { Banner } from '@/components/banner';
import { MaterialSymbolIcon } from '@/components/material-symbol-link';
import clsx from 'clsx';
import { useState } from 'react';
import { VisitTitleButtons } from '@/components/navigation/visit-title-buttons';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useEncounter } from '@/contexts/encounter-context';
import { SaveProgressButton } from '@/components/navigation/save-progress-button';
import { Button } from '@/components/button';
import { Navigate, useLocation } from '@/util/react-router-dom';
import { useBranding } from '@/outlets/branding-outlet';
import { VisitNavigation } from '@/components/navigation/visit-navigation';
import z from 'zod';
import { InsuranceCoverageModal } from '@/components/encounter/insurance/insurance-coverage-modal';
import { createEditUrl } from '@/util/helpers';
import { useUpdateEncountersStatus } from '@/api/update-encounter-status';
import { EncounterStatusIdsType } from '@/api/api.schemas';
import { usePostSubmitNavigation } from '@/hooks/use-post-submit-navigation';
import { useVisitNavigation } from '@/contexts/visit-navigation-context';
import { SuccessCircleSvg } from '@/components/svgs/success-circle';
import { CancelSvg } from '@/components/svgs/cancel';
import { formatPrice } from '@/util/formats';
import { Separator } from '@/components/separator';
import styles from './insurance-coverage-page.module.css';
import { useAsyncAction } from '@/hooks/use-async-action';
import { getInsuranceCoverageDisplayConfig } from './insurance-coverage-config';

const insuranceCoverageStateSchema = z.object({
  insuranceActive: z.boolean(),
  visitPrice: z.number(),
  estimatedCost: z.number(),
  insuranceCoverage: z.number(),
  hasVisitWithinSevenDays: z.boolean(),
  isGovernmentProgram: z.boolean(),
  requiresPcpSpecialistReferral: z.boolean(),
});

const translationBase = 'visit.insuranceInformation';
const coverageTranslationBase = `${translationBase}.coverage`;

export function InsuranceCoveragePage() {
  const { encounter, invalidateEncounter } = useEncounter();
  const { t } = useTranslation();
  const { displayOncePerWeekInsuranceDisclaimer } = useBranding();
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useMediaQuery('--screen-md');
  const { state } = useLocation();
  const { editMode } = useVisitNavigation();
  useUpdateEncountersStatus();
  const { mutateAsync: updateEncounterStatus, isPending } = useUpdateEncountersStatus();
  const { navigatePostSubmit } = usePostSubmitNavigation();

  const stateValidation = insuranceCoverageStateSchema.safeParse(state);

  const { trigger: handleConfirm } = useAsyncAction(async () => {
    if (!editMode) {
      await updateEncounterStatus({
        encounterId: encounter.id,
        data: {
          statusId: EncounterStatusIdsType.ConditionInformationPage1,
        },
      });

      invalidateEncounter();
    }

    navigatePostSubmit();
  });

  if (!stateValidation.success) {
    return <Navigate to={`/visit/${encounter.id}/insurance-information`} replace />;
  }

  const {
    insuranceActive,
    isGovernmentProgram,
    requiresPcpSpecialistReferral,
    hasVisitWithinSevenDays,
    visitPrice,
    insuranceCoverage,
    estimatedCost,
  } = stateValidation.data;

  const backUrl = createEditUrl(`/visit/${encounter.id}/insurance-information`, editMode);

  const displayConfig = getInsuranceCoverageDisplayConfig({
    t,
    insuranceActive,
    isGovernmentProgram,
    requiresPcpSpecialistReferral,
    hasVisitWithinSevenDays,
    displayOncePerWeekInsuranceDisclaimer,
  });

  return (
    <PageContainer>
      {!isMobile && <VisitTitleButtons showBackButtonOnReview backButton={{ to: backUrl }} />}
      <GradientFormTitle>
        {isMobile && <VisitTitleButtons showBackButtonOnReview backButton={{ to: backUrl }} />}
        <VisitNavigation />
        <h1 className="h3">{t(`${coverageTranslationBase}.pageTitle`)}</h1>
        {isMobile && <SaveProgressButton />}
      </GradientFormTitle>

      <Section className={styles.coverageContainer}>
        <div className={styles.textContainer}>
          <div className={styles.descriptionContainer}>
            <div className={styles.titleContainer}>
              {displayConfig.isSuccess ? (
                <SuccessCircleSvg className={styles.icon} />
              ) : (
                <CancelSvg className={styles.icon} />
              )}
              <h1 className="h3">{displayConfig.title}</h1>
            </div>
            <p className="body-1">{displayConfig.description}</p>
          </div>
          <div className={styles.priceContainer}>
            <div className={styles.priceRow}>
              <p className={clsx('body-3', styles.rowTitle)}>
                {t(`${coverageTranslationBase}.price.standardPrice`)}
              </p>
              <p>{formatPrice(visitPrice)}</p>
            </div>
            <div className={styles.priceRow}>
              <p className={clsx('body-3', styles.rowTitle)}>
                {t(`${coverageTranslationBase}.price.coverage`)}
                <MaterialSymbolIcon
                  name="info"
                  className={styles.infoIcon}
                  ariaLabel={`${coverageTranslationBase}.price.infoLabel`}
                  onClick={() => setIsOpen(true)}
                />
              </p>
              <p>{formatPrice(insuranceCoverage)}</p>
            </div>
            <Separator className={styles.separator} />

            <div className={styles.priceRow}>
              <p className={clsx('body-3', styles.rowTitle)}>
                {t(`${coverageTranslationBase}.price.estimatedCost`)}
              </p>
              <p className={clsx('body-1', 'bold')}>{formatPrice(estimatedCost)}</p>
            </div>
          </div>
        </div>
        {displayConfig.banner && (
          <Banner
            type="warning"
            label={displayConfig.banner.label}
            contentClassName={styles.bannerContent}
            labelClassName={styles.bannerLabel}
          >
            <p className={clsx('body-3', styles.bannerDescription)}>
              {displayConfig.banner.description}
            </p>
          </Banner>
        )}
      </Section>

      <InsuranceCoverageModal isOpen={isOpen} onClose={() => setIsOpen(false)} />

      <VisitFormButtonRow
        nextStep={t(`${translationBase}.nextStep`)}
        buttonTextKey="common.continue"
        onClick={handleConfirm}
        loading={isPending}
        reviewButtonTextKey="common.continue"
        secondaryButton={
          <Button as="a" to={backUrl} variant="outlined" color="accent">
            {t(`${coverageTranslationBase}.changeInsurance`)}
          </Button>
        }
      />
    </PageContainer>
  );
}
